import { z } from 'zod';

export type PermissionScope = { warehouseIds: string[] | 'ALL'; partyIds: string[] | 'ALL'; organizationIds?: string[] | 'ALL' };

/** A permission code together with the access scope of the role assignment that granted it. */
export interface PermissionGrant {
  code: string;
  warehouseIds: string[] | 'ALL';
  partyIds: string[] | 'ALL';
  organizationIds?: string[] | 'ALL';
}

export type RoleProfile = 'operator' | 'supervisor' | 'manager' | 'customer' | 'carrier' | 'driver' | 'analyst' | 'executive' | 'admin';

export interface SessionUser {
  id: string;
  username: string;
  displayName: string;
  email?: string;
  avatarUrl?: string;
  locale?: string;
  timeZone?: string;
}

export interface SessionTenant {
  id: string;
  code: string;
  name: string;
  defaultWarehouseId?: string;
  features?: Record<string, boolean>;
}

export interface SessionRole {
  code: string;
  name: string;
  uiProfile?: RoleProfile;
}

export interface SessionWarehouse {
  id: string;
  code: string;
  name: string;
  /** L-13c (G-132): where the site is, for the scope menu. */
  city?: string | null;
  countryCode?: string | null;
}

/** Body of `GET /api/v1/identity/me`. */
export interface MeResponse {
  user: SessionUser;
  tenant: SessionTenant;
  roles: SessionRole[];
  permissions: PermissionGrant[];
  uiProfile?: RoleProfile;
  warehouses?: SessionWarehouse[];
}

export const roleProfileSchema = z.enum(['operator', 'supervisor', 'manager', 'customer', 'carrier', 'driver', 'analyst', 'executive', 'admin']);

const scopeSetSchema = z.union([z.literal('ALL'), z.array(z.string())]);

export const permissionGrantSchema: z.ZodType<PermissionGrant> = z.object({
  code: z.string().min(1),
  warehouseIds: scopeSetSchema.default('ALL'),
  partyIds: scopeSetSchema.default('ALL'),
  organizationIds: scopeSetSchema.optional(),
}) as unknown as z.ZodType<PermissionGrant>;

export const sessionUserSchema: z.ZodType<SessionUser> = z
  .object({
    id: z.string(),
    username: z.string(),
    displayName: z.string(),
    email: z.string().optional(),
    avatarUrl: z.string().optional(),
    locale: z.string().optional(),
    timeZone: z.string().optional(),
  })
  .loose() as unknown as z.ZodType<SessionUser>;

export const sessionTenantSchema: z.ZodType<SessionTenant> = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    defaultWarehouseId: z.string().optional(),
    features: z.record(z.string(), z.boolean()).optional(),
  })
  .loose() as unknown as z.ZodType<SessionTenant>;

export const sessionRoleSchema: z.ZodType<SessionRole> = z
  .object({
    code: z.string(),
    name: z.string().default(''),
    uiProfile: roleProfileSchema.optional(),
  })
  .loose() as unknown as z.ZodType<SessionRole>;

export const sessionWarehouseSchema: z.ZodType<SessionWarehouse> = z
  .object({ id: z.string(), code: z.string(), name: z.string() })
  .loose() as unknown as z.ZodType<SessionWarehouse>;

export const meResponseSchema: z.ZodType<MeResponse> = z
  .object({
    user: sessionUserSchema,
    tenant: sessionTenantSchema,
    roles: z.array(sessionRoleSchema).default([]),
    permissions: z.array(permissionGrantSchema).default([]),
    uiProfile: roleProfileSchema.optional(),
    warehouses: z.array(sessionWarehouseSchema).optional(),
  })
  .loose() as unknown as z.ZodType<MeResponse>;
